import type { Editor as TipTapEditor } from "@tiptap/core";
import { MenuItem } from "primereact/menuitem";
import { TooltipOptions } from "primereact/tooltip/tooltipoptions";
import { canInsertCallout, insertCallout } from './callout-insertion';
export { canInsertCallout, insertCallout };

export const applyClass = (editor: TipTapEditor, className: string) => {
    const { from, to } = editor.state.selection;
    const hasSelection = to > from;

    if (hasSelection) {
        // Apply to selected text (inline mark)
        editor.chain().focus().setMark("classMark", { class: className }).run();
    } else {
        // Apply to the block node the cursor is inside
        const block = editor.state.selection.$from.parent;
        const type = block.type.name;

        editor.chain().focus().updateAttributes(type, { class: className }).run();
    }
};

export const clearClass = (editor: TipTapEditor) => {
    const { from, to } = editor.state.selection;
    const hasSelection = to > from;

    if (hasSelection) {
        editor.chain().focus().unsetMark("classMark").run();
    } else {
        const block = editor.state.selection.$from.parent;
        const type = block.type.name;

        editor.chain().focus().updateAttributes(type, { class: null }).run();
    }
};

export function applyBlockClass(editor: TipTapEditor, className: string) {
    const { state, view } = editor;
    const { tr, selection } = state;
    const { $from } = selection;

    // Find the parent block node
    const blockPos = $from.before($from.depth);
    const blockNode = $from.node($from.depth);

    if (!blockNode) return;

    // Replace the node with updated attrs
    const newAttrs = {
        ...blockNode.attrs,
        class: className
    };

    const newNode = blockNode.type.create(newAttrs, blockNode.content, blockNode.marks);

    view.dispatch(
        tr.replaceWith(blockPos, blockPos + blockNode.nodeSize, newNode)
    );

    view.focus();
}

export function insertBadge(editor: TipTapEditor, className: string, text: string) {
    const { state } = editor;
    const isAtEnd = state.selection.to >= state.doc.content.size - 1;

    editor
        .chain()
        .insertContent(`<span class="${className}">${text}</span> `)
        .run();

    if (isAtEnd) {
        editor
            .chain()
            .insertContent({ type: 'paragraph' })
            .focus()
            .run();
    }
}

export function insertCollapsibleSection(editor: TipTapEditor) {
  editor.chain().insertContent({
    type: "collapsible",
    content: [
        {
        type: "collapsibleHeader",
        content: [
            {
            type: "collapsibleHeaderTitle",
            content: [{ type: "text", text: "Show more" }]
            }
        ]
        },
        {
        type: "collapsibleBody",
        content: [
            { type: "paragraph", content: [{ type: "text", text: "content" }] }
        ]
        }
    ]
    }).run();
}

export function clearFormatting(editor: TipTapEditor) {
  const chain = editor.chain().focus();
  chain.unsetAllMarks();
  chain.setParagraph();
  chain.setTextAlign('');
  chain.updateAttributes('paragraph', { class: null });
  chain.setFontFamily('');
  chain.unsetHighlight();
  chain.updateAttributes('paragraph', { class: null });
  return chain.run();
}

export const getTooltipOptions = (): TooltipOptions => {
    return {
        position: "bottom",
        style: { width: 'max-content', maxWidth: 'min(24rem, calc(100vw - 1rem))' },
    };
}

export const getFontsModel = (editor: TipTapEditor): MenuItem[] => {
    return [
        { label: "Arial", command: () => editor.chain().focus().setFontFamily("arial").run() },
        { label: "Helvetica", command: () => editor.chain().focus().setFontFamily("helvetica").run() },
        { label: "Inter", command: () => editor.chain().focus().setFontFamily("inter").run() },
        { label: "Roboto", command: () => editor.chain().focus().setFontFamily("roboto").run() },
        { label: "Georgia", command: () => editor.chain().focus().setFontFamily("georgia").run() },
        { label: "Times New Roman", command: () => editor.chain().focus().setFontFamily("times").run() },
        { label: "Monospace", command: () => editor.chain().focus().setFontFamily("mono").run() },
    ];
}

export const getHeadingModel = (editor: TipTapEditor): MenuItem[] => {
    return [
        { label: "Paragraph", command: () => editor.chain().focus().setParagraph().run() },
        { label: "Heading 1", command: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
        { label: "Heading 2", command: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
        { label: "Heading 3", command: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
        { label: "Heading 4", command: () => editor.chain().focus().toggleHeading({ level: 4 }).run() },
        { label: "Heading 5", command: () => editor.chain().focus().toggleHeading({ level: 5 }).run() },
        { label: "Heading 6", command: () => editor.chain().focus().toggleHeading({ level: 6 }).run() },

        { separator: true },
        
        { label: "Styles", items: getStylesModel(editor) },

        { separator: true },

        { label: "Highlight", items: [
                { label: "Warning", command: () => applyClass(editor, "bg-highlight-warning") },
                { label: "Info",    command: () => applyClass(editor, "bg-highlight-info") },
                { label: "Success", command: () => applyClass(editor, "bg-highlight-success") },
                { label: "Danger",  command: () => applyClass(editor, "bg-highlight-danger") },
                { label: "Muted",   command: () => applyClass(editor, "bg-highlight-muted") },
            ]
         },

        { label: "Marker", command: () => editor.chain().focus().toggleHighlight({ color: "yellow" }).run() },
        { label: "Remove marker", command: () => editor.chain().focus().unsetHighlight().run() },

        { separator: true },

        { label: "Clear formatting", command: () => clearFormatting(editor) },
    ];
}

export const getAlignModel = (editor: TipTapEditor): MenuItem[] => {
    return [
        { label: "Align left", icon: "pi pi-align-left", command: () => editor.chain().focus().setTextAlign("left").run() },
        { label: "Align center", icon: "pi pi-align-center", command: () => editor.chain().focus().setTextAlign("center").run() },
        { label: "Align right", icon: "pi pi-align-right", command: () => editor.chain().focus().setTextAlign("right").run() },
        { label: "Justify", icon: "pi pi-align-justify", command: () => editor.chain().focus().setTextAlign("justify").run() },
    ];
}

export const getInsertModel = (editor: TipTapEditor, calloutInsertionAllowed = canInsertCallout(editor)): MenuItem[] => {
    return [
        { label: "Table", icon: "pi pi-table", command: () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .updateAttributes('table', { 
                tableWidth: '100%', 
                tableAlign: 'left' 
        }).run() },
        // { label: "Divider", icon: "pi pi-minus", command: () => editor.chain().focus().setHorizontalRule().run() },
        {
            label: "Divider",
            items: [
                { label: "Default", command: () => editor.chain().focus().insertHR("default").run() },
                { label: "Subtle",  command: () => editor.chain().focus().insertHR("subtle").run() },
                { label: "Strong",  command: () => editor.chain().focus().insertHR("strong").run() },
                { label: "Dotted",  command: () => editor.chain().focus().insertHR("dotted").run() },
                { label: "Dashed",  command: () => editor.chain().focus().insertHR("dashed").run() },
                { label: "Info",    command: () => editor.chain().focus().insertHR("info").run() },
                { label: "Warning", command: () => editor.chain().focus().insertHR("warning").run() },
                { label: "Danger",  command: () => editor.chain().focus().insertHR("danger").run() },
            ],
        },

        { separator: true },
        
        { label: "Callouts", disabled: !calloutInsertionAllowed, items: [
            { label: "Info callout",    icon: "pi pi-info", command: () => insertCallout(editor, "info") },
            { label: "Success callout", command: () => insertCallout(editor, "success") },
            { label: "Warning callout", command: () => insertCallout(editor, "warning") },
            { label: "Danger callout",  icon: "pi pi-exclamation-triangle", command: () => insertCallout(editor, "danger") },
            { label: "Muted callout",   command: () => insertCallout(editor, "muted") },
        ],
        },
        { separator: true },
            
        { label: "Badges", items: [
            { label: "Primary badge", command: () => insertBadge(editor, "badge badge-primary", "New") },
            { label: "Success badge", command: () => insertBadge(editor, "badge badge-success", "Success") },
            { label: "Info badge",    icon: "pi pi-info-circle", command: () => insertBadge(editor, "badge badge-info", "Info") },
            { label: "Warning badge", command: () => insertBadge(editor, "badge badge-warning", "Warning") },
            { label: "Danger badge",  icon: "pi pi-exclamation-circle", command: () => insertBadge(editor, "badge badge-danger", "Danger!") },
        ]
        },
    ];
}

export const getTableModel = (editor: TipTapEditor, openTablePropertiesDialog: () => void): MenuItem[] => {
    return [
        { label: "Insert row above", command: () => editor.chain().focus().addRowBefore().run() },
        { label: "Insert row below", command: () => editor.chain().focus().addRowAfter().run() },
        { label: "Insert column left", command: () => editor.chain().focus().addColumnBefore().run() },
        { label: "Insert column right", command: () => editor.chain().focus().addColumnAfter().run() },
        { separator: true },
        { label: "Delete row", command: () => editor.chain().focus().deleteRow().run() },
        { label: "Delete column", command: () => editor.chain().focus().deleteColumn().run() },
        { label: "Delete table", command: () => editor.chain().focus().deleteTable().run() },
        { separator: true },
        { label: "Merge cells", command: () => editor.chain().focus().mergeCells().run() },
        { label: "Split cell", command: () => editor.chain().focus().splitCell().run() },
        { separator: true },
        { label: "Toggle header row", command: () => editor.chain().focus().toggleHeaderRow().run() },
        { label: "Toggle header column", command: () => editor.chain().focus().toggleHeaderColumn().run() },
        { separator: true },
        { label: "Table properties…", command: () => openTablePropertiesDialog() },
    ];
}

export const getStylesModel = (editor: TipTapEditor): MenuItem[] => {

    return [
        // TEXT COLORS
        { label: "Muted text",    command: () => applyClass(editor, "text-muted") },
        { label: "Secondary text", command: () => applyClass(editor, "text-secondary") },
        { label: "Success text",  command: () => applyClass(editor, "text-success") },
        { label: "Info text",     command: () => applyClass(editor, "text-info") },
        { label: "Warning text",  command: () => applyClass(editor, "text-warning") },
        { label: "Danger text",   command: () => applyClass(editor, "text-danger") },

        { separator: true },

        // TEXT SIZE
        { label: "Small text", command: () => applyClass(editor, "text-sm") },
        { label: "Large text", command: () => applyClass(editor, "text-lg") },

        { separator: true },

        // SPACING UTILITIES
        { label: "Margin small",  command: () => applyClass(editor, "m-2") },
        { label: "Margin medium", command: () => applyClass(editor, "m-3") },
        { label: "Margin large",  command: () => applyClass(editor, "m-4") },

        { label: "Padding small",  command: () => applyClass(editor, "p-2") },
        { label: "Padding medium", command: () => applyClass(editor, "p-3") },
        { label: "Padding large",  command: () => applyClass(editor, "p-4") },

        { separator: true },

        // CLEAR
        { label: "Clear style", command: () => clearClass(editor) },
    ];
};
